import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
// @mui
import FormHelperText from '@mui/material/FormHelperText';
//
import { getCurrentProject, getCurrentServer, getMandEndPoint } from 'src/helper/server-address';
import axiosInstance from 'src/api/axiosInstance';
import { UploadAvatar, Upload, UploadBox } from '../upload';

export function RHFUploadAvatar({ name, acceptFile, maxSize = 3 * 1024 * 1024, ...other }) {
  const { control } = useFormContext();

  const user = JSON.parse(localStorage.getItem('user'));

  const globalMaxSize = useSelector((state) => state.tenantPreference?.maxSizeOfDocuments);
  const effectiveMaxSize = (globalMaxSize || 3) * 1024 * 1024;

  const getFileErrorMessage = (file) => {
    if (file?.size > effectiveMaxSize) {
      return `File exceeds the maximum size of ${(effectiveMaxSize / (1024 * 1024)).toFixed(2)} MB.`;
    }
    return null;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const fileErrorMessage = field.value ? getFileErrorMessage(field.value) : null;

        return (
          <div>
            <UploadAvatar
              error={!!error || !!fileErrorMessage}
              file={field.value}
              maxSize={effectiveMaxSize}
              helperText={
                <>
                  {!!error && (
                    <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                      {error.message}
                    </FormHelperText>
                  )}
                  {!!fileErrorMessage && (
                    <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                      {fileErrorMessage}
                    </FormHelperText>
                  )}
                </>
              }
              {...field}
              {...other}
            />
          </div>
        );
      }}
    />
  );
}

RHFUploadAvatar.propTypes = {
  name: PropTypes.string.isRequired,
  acceptFile: PropTypes.object.isRequired,
  maxSize: PropTypes.number,
};

export function RHFUploadBox({
  name,
  helperText,
  acceptFile,
  maxSize = 3 * 1024 * 1024,
  ...other
}) {
  const { control } = useFormContext();
  const globalMaxSize = useSelector((state) => state.tenantPreference?.maxSizeOfDocuments);
  const maxSizeOfDocs = (globalMaxSize || 3) * 1024 * 1024;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <UploadBox
          acceptFile={acceptFile}
          file={field.value}
          error={!!error}
          helperText={
            (!!error || helperText) && (
              <FormHelperText error={!!error} sx={{ px: 2 }}>
                {error ? error?.message : helperText}
              </FormHelperText>
            )
          }
          {...other}
          maxSize={maxSizeOfDocs}
        />
      )}
    />
  );
}

RHFUploadBox.propTypes = {
  helperText: PropTypes.string,
  name: PropTypes.string.isRequired,
  acceptFile: PropTypes.object.isRequired,
  maxSize: PropTypes.number,
};

export function RHFUpload({ name, multiple, acceptFile, helperText, ...other }) {
  const { control } = useFormContext();

  const globalMaxSize = useSelector((state) => state.tenantPreference?.maxSizeOfDocuments);
  const maxSizeOfDocs = (globalMaxSize || 3) * 1024 * 1024;

  if (!globalMaxSize && globalMaxSize !== 0) {
    // We don't really need a loading state anymore, since Redux gives it synchronously,
    // but just to be safe if the default state isn't initialized yet.
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) =>
        multiple ? (
          <Upload
            multiple
            accept={acceptFile}
            files={field.value}
            error={!!error}
            helperText={
              (!!error || helperText) && (
                <FormHelperText error={!!error} sx={{ px: 2 }}>
                  {error ? error?.message : helperText}
                </FormHelperText>
              )
            }
            {...other}
            maxSize={maxSizeOfDocs}
          />
        ) : (
          <Upload
            file={field.value}
            accept={acceptFile}
            error={!!error}
            helperText={
              (!!error || helperText) && (
                <FormHelperText error={!!error} sx={{ px: 2 }}>
                  {error ? error?.message : helperText}
                </FormHelperText>
              )
            }
            {...other}
            maxSize={maxSizeOfDocs}
          />
        )
      }
    />
  );
}

RHFUpload.propTypes = {
  name: PropTypes.string.isRequired,
  multiple: PropTypes.bool,
  acceptFile: PropTypes.object.isRequired,
  helperText: PropTypes.string,
};